import {Options} from "highcharts";
import {registerPlugin} from "surveylabJsAssets/chart/charts";
import AbstractChart from "surveylabJsAssets/chart/plugin/abstract-chart";
import * as Highcharts from 'highcharts/highcharts';

// custom "x" symbol
Highcharts.SVGRenderer.prototype.symbols.x = function (
    x: number,
    y: number,
    w: number,
    h: number
): Highcharts.SVGPathArray {
    const shrink = 0.5;
    const xPad = (w * (1 - shrink)) / 2;
    const yPad = (h * (1 - shrink)) / 2;
    return [
        ['M', x + xPad, y + yPad],
        ['L', x + w - xPad, y + h - yPad],
        ['M', x + w - xPad, y + yPad],
        ['L', x + xPad, y + h - yPad]
    ];
};

export class Responses extends AbstractChart {

    protected allChartOptions(): Options {
        const normInfo = $(this.target).data('norm') as {
            label: string;
            sampleSizes: number[],
            sampleLabels: string[]
        };
        const labels = $(this.target).data('labels') as {
            value: string;
            ranges: string,
            sample: string
        };
        const bands = $(this.target).data('bands') as {
            from: number;
            to: number;
            pos: number;
            label: string;
            color: string;
            lineWidth: number;
            lineColor: string;
        }[];

        const options: Options = {
            chart: {
                type: 'column',
                height: 520,
                width: 540,
                marginLeft: 130,
                marginTop: 15,
                marginBottom: 60,
                // Remove the default full-frame border; we'll add side borders via axis lines
                plotBorderWidth: 0,
                style: {
                    fontFamily: 'Arial, sans-serif'
                },
                ignoreHiddenSeries: true,
                events: {
                    render: function () {
                        const chart = this as Highcharts.Chart & { __rightPanelEls?: Highcharts.SVGElement[] };
                        // clean up existing custom elements
                        if (chart.__rightPanelEls) {
                            chart.__rightPanelEls.forEach(el => el.destroy());
                        }
                        chart.__rightPanelEls = [];

                        // Draw right-side background bands behind the opposite labels
                        const panelLeft = chart.plotLeft + chart.plotWidth; // right edge of plot area
                        const rightX = panelLeft;
                        // Use the available area to the right of the plot up to spacingRight
                        const panelWidth = Math.max(160, Math.min(260, chart.chartWidth - panelLeft));

                        // Draw rectangles by mirroring the ACTUAL plotBands geometry from the left yAxis.
                        // This guarantees pixel-perfect alignment regardless of Highcharts' internal rounding.
                        const axisAny = chart.yAxis[0] as any;
                        const items: any[] = (axisAny && axisAny.plotLinesAndBands) ? axisAny.plotLinesAndBands : [];
                        items.forEach((it: any, idx: number) => {
                            // Only bands (have both from and to)
                            const opt = it && it.options ? it.options : {};
                            if (typeof opt.from === 'number' && typeof opt.to === 'number') {
                                const svg = it.svgElem;
                                if (!svg || typeof (svg as any).getBBox !== 'function') {
                                    return;
                                }
                                const bb = (svg as any).getBBox();
                                // Clamp within plot area in case of fractional overflow
                                const plotTop = chart.plotTop;
                                const plotBottom = chart.plotTop + chart.plotHeight;
                                const yTop = Math.max(plotTop, Math.min(plotBottom, bb.y));
                                const yBottom = Math.max(plotTop, Math.min(plotBottom, bb.y + bb.height));
                                // Use asymmetric rounding to keep top aligned and extend height to the exact bottom
                                // This avoids bands appearing a few pixels shorter on the right side.
                                const y = yTop;
                                const h = Math.max(0, yBottom - y);
                                const color = opt.color || (bands && bands[idx] ? bands[idx].color : '#eee');
                                const rect = chart.renderer
                                    .rect(rightX, y, panelWidth, h)
                                    .attr({fill: color, zIndex: 0})
                                    .add();
                                chart.__rightPanelEls!.push(rect);
                            }
                        });

                        // Add vertical "T-Wert" label on the left side, centered with the chart
                        // Place it just left of the left axis labels, vertically centered to the plot area
                        const leftLabelText = labels.value || 'T-Wert';
                        const vcx = chart.plotTop + (chart.plotHeight / 2);
                        // Heuristic horizontal offset: a bit left from the plotLeft to clear tick labels
                        const leftOffset = 58; // tweak if needed
                        const lx = chart.plotLeft - leftOffset;
                        const tLabel = chart.renderer
                            .text(leftLabelText, lx, vcx)
                            .attr({
                                rotation: 270,
                                zIndex: 2,
                            })
                            .css({
                                fontSize: '13px',
                                fontWeight: 'bold',
                                color: '#333',
                                textAlign: 'center'
                            })
                            .add();
                        // Align baseline to center vertically by translating to its bounding box center
                        const bb = (tLabel as any).getBBox ? (tLabel as any).getBBox() : {y: 0, height: 0};
                        // Because rotation is applied around text's origin, nudge position so visual center is at vcx
                        const adjustY = vcx - (bb.y + bb.height / 2);
                        tLabel.translate(0, adjustY);
                        chart.__rightPanelEls!.push(tLabel);
                    }
                }
            },
            title: {text: null},
            xAxis: [
                {
                    visible: true,
                    categories: normInfo.sampleLabels,
                    min: 0,
                    max: Math.max(0, (normInfo.sampleLabels?.length || 1) - 1),
                    title: {text: undefined},
                    // No bottom border
                    lineWidth: 0,
                    labels: {
                        enabled: true,
                        useHTML: true,
                        style: {fontSize: '14px', color: '#000'},
                        formatter: function () {
                            const i = this.pos as number;
                            const name = normInfo.sampleLabels[i] || '';
                            const count = normInfo.sampleSizes[i] ?? '';
                            return `<div style="text-align:center;line-height:1.1">${name}<br/><span style="font-weight:normal">${count}</span></div>`;
                        }
                    },
                    plotLines: []
                }
            ],
            yAxis: [
                {
                    min: 10,
                    max: 90,
                    tickInterval: 10,
                    reversed: false,
                    // Small connector lines between left-side numeric labels and the plot (as in the reference image)
                    // Short lines between the numeric labels (left) and the axis line.
                    // Render them outside so they sit between labels and the axis, matching the reference image.
                    tickLength: 12,
                    tickWidth: 2,
                    tickColor: '#333',
                    tickPosition: 'outside',
                    // Left-side border
                    lineWidth: 2,
                    lineColor: '#333',
                    // Suppress default axis title text; we draw a custom centered vertical label in render()
                    title: {
                        text: undefined,
                        rotation: 270,
                        align: 'middle',
                        offset: 30,
                        y: 0,
                        x: -70,
                        style: {
                            fontSize: '11px',
                            fontWeight: 'bold'
                        }
                    },
                    labels: {
                        enabled: true,
                        style: {
                            fontSize: '13px',
                            color: '#333'
                        },
                        x: -17,
                        formatter: function () {
                            return this.pos.toString();
                        }
                    },
                    gridLineWidth: 0,
                    plotBands: bands.map(b => ({
                        from: b.from,
                        to: b.to,
                        color: b.color,
                        zIndex: 0
                    })),
                },
                {
                    opposite: true,
                    linkedTo: 0,
                    min: 10,
                    max: 90,
                    tickPositions: [85, 75, 65, 50, 35, 25, 15],
                    reversed: false,
                    // Right-side border
                    lineWidth: 2,
                    lineColor: '#333',
                    title: {
                        text: labels.ranges,
                        rotation: 0,
                        align: 'high',
                        offset: 0,
                        y: -25,
                        x: 10,
                        style: {
                            fontSize: '13px',
                            color: '#333',
                        }
                    },
                    labels: {
                        enabled: true,
                        reserveSpace: true,
                        style: {
                            fontSize: '13px',
                            color: '#333',
                            width: 180,
                            whiteSpace: 'nowrap'
                        },
                        x: 8,
                        formatter: function () {
                            let label = '';
                            bands.forEach(band => {
                                if (band.pos === this.pos) {
                                    label = band.label;
                                }
                            });
                            return label;
                        }
                    },
                    gridLineWidth: 0,
                },
            ],
            tooltip: {
                enabled: false
            },
            plotOptions: {
                series: {
                    animation: false
                },
                column: {
                    color: '#f5b900',
                    borderColor: '#e5e5e5',
                    // Make columns visibly wider
                    pointPadding: 0,
                    groupPadding: 0.01,
                    // Force a fixed column width to ensure wide yellow bars regardless of chart width
                    pointWidth: 60,
                    // Cap the maximum width in case of very wide layouts
                    maxPointWidth: 70,
                    dataLabels: {
                        enabled: true,
                        // Remove any white outline/drop shadow around the numbers
                        style: {
                            fontSize: '16px',
                            fontWeight: 'bold',
                            color: '#000',
                            textOutline: 'none',
                            textShadow: 'none'
                        },
                        formatter: function () {
                            return (this.y as number).toString();
                        },
                        inside: true,
                        align: 'center',
                        verticalAlign: 'top',
                        y: 8
                    }
                }
            },
            credits: {enabled: false},
            exporting: {enabled: false}
        };

        let baseOptions: Options = super.allChartOptions();
        delete baseOptions.yAxis;

        return super.mergeOptions(super.allChartOptions(), options);
    }
}

export default function () {
    registerPlugin(Responses);
}
